import { useState } from "react";
import { Stat } from "./DailyView";

function PeriodChip({ active, label, sub, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title={sub}
      className={`px-3 py-1.5 text-xs font-medium border transition ${
        active ? "bg-[var(--maroon)] text-white border-[var(--maroon)]" : "bg-white text-[var(--ink-soft)] border-[var(--parchment-2)] hover:border-[var(--ink)]"
      }`}
    >
      {label}
    </button>
  );
}

function CountBadge({ count, color }) {
  if (count === 0) return <span className="text-[var(--muted)] font-mono-spec">0</span>;
  return (
    <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 text-white font-mono-spec font-semibold text-xs" style={{ background: color }}>
      {count}
    </span>
  );
}

const countsFor = (r, periodKey) => {
  if (periodKey === "all") return { abs: r.absences_count, ret: r.retards_count, days: r.absences_days_count || 0, just: r.absences_just_count || 0 };
  return {
    abs: r.absences_by_period?.[periodKey] || 0,
    ret: r.retards_by_period?.[periodKey] || 0,
    days: r.absences_days_by_period?.[periodKey] || 0,
    just: r.absences_just_by_period?.[periodKey] || 0,
  };
};

function LastEventCell({ r }) {
  if (r.last_absence) return <span>Absent · {r.last_absence}</span>;
  if (r.last_retard) return <span>Retard · {r.last_retard}</span>;
  return <span className="text-[var(--ok)]">Aucun</span>;
}

function RecapRow({ r, index, periodKey }) {
  const c = countsFor(r, periodKey);
  return (
    <tr className="border-b border-[var(--parchment-2)]/60" data-testid={`recap-row-${r.student.id}`}>
      <td className="py-3 font-mono-spec text-xs text-[var(--muted)]">{String(index + 1).padStart(2, "0")}</td>
      <td className="py-3">{r.student.last_name.toUpperCase()} {r.student.first_name}</td>
      <td className="py-3 text-center"><CountBadge count={c.ret} color="var(--just)" /></td>
      <td className="py-3 text-center"><CountBadge count={c.abs} color="var(--late)" /></td>
      <td className="py-3 text-center"><CountBadge count={c.just} color="var(--ok)" /></td>
      <td className="py-3 text-center"><CountBadge count={c.days} color="var(--maroon)" /></td>
      <td className="py-3 text-xs text-[var(--ink-soft)] font-mono-spec"><LastEventCell r={r} /></td>
    </tr>
  );
}

export default function RecapView({ recap, loading }) {
  const [periodKey, setPeriodKey] = useState("all");

  if (loading || !recap) {
    return <div className="text-[var(--ink-soft)]">Chargement...</div>;
  }

  const periods = recap.periods || [];
  const totalAbs = recap.rows.reduce((acc, r) => acc + countsFor(r, periodKey).abs, 0);
  const totalRet = recap.rows.reduce((acc, r) => acc + countsFor(r, periodKey).ret, 0);
  const periodLabel = periodKey === "all" ? "année scolaire" : periods.find((p) => p.key === periodKey)?.name;

  return (
    <div className="bg-white border border-[var(--parchment-2)] p-6" data-testid="appel-recap">
      <div className="flex flex-wrap items-end justify-between gap-4 pb-4 border-b border-[var(--parchment-2)]">
        <div>
          <div className="eyebrow">Bilan cumulé · {periodLabel}</div>
          <h2 className="font-serif-display text-2xl font-semibold mt-1">Absences et retards</h2>
        </div>
        <div className="flex gap-6 text-sm">
          <Stat label="Total retards (créneaux)" value={totalRet} color="var(--just)" testId="recap-total-retards" />
          <Stat label="Total absences (créneaux)" value={totalAbs} color="var(--late)" testId="recap-total-absences" />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="period-filter">
        <PeriodChip active={periodKey === "all"} label="Année complète" onClick={() => setPeriodKey("all")} testId="period-chip-all" />
        {periods.map((p) => (
          <PeriodChip key={p.key} active={periodKey === p.key} label={p.name} sub={`${p.start} → ${p.end}`} onClick={() => setPeriodKey(p.key)} testId={`period-chip-${p.key}`} />
        ))}
      </div>

      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-sm" data-testid="recap-table">
          <thead>
            <tr className="border-b border-[var(--parchment-2)] text-left">
              <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">#</th>
              <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Élève</th>
              <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium text-center">Retards</th>
              <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium text-center">Abs. (créneaux)</th>
              <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium text-center">Abs. just.</th>
              <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium text-center">Abs. (jours)</th>
              <th className="py-2 eyebrow !text-[var(--ink-soft)] font-medium">Dernier événement</th>
            </tr>
          </thead>
          <tbody>
            {recap.rows.map((r, i) => <RecapRow key={r.student.id} r={r} index={i} periodKey={periodKey} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
